import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { QRCodeSVG } from "qrcode.react";
import {
  LIVE_POLL_MS,
  NOT_PERSISTED,
  queryPolicies,
} from "@sx/api-client";
import { Button, QueryErrorState, StatusDot } from "@sx/ui";

import { doorPrefix } from "../lib/doors";
import {
  beginHeadsetSignIn,
  claimPhoneOffer,
  codeRefreshAt,
  headsetName,
  openHeadsetSession,
  pairOfferFrom,
  pollHeadsetSignIn,
  safeNext,
  shortAddress,
  type HeadsetCode,
  type HeadsetPoll,
} from "../lib/headset-sign-in";
import { authQueryKeys } from "../lib/query-keys";

interface HeadsetSearch {
  readonly next?: string;
}

export const Route = createFileRoute("/headset")({
  validateSearch: (search: Record<string, unknown>): HeadsetSearch =>
    typeof search.next === "string" ? { next: search.next } : {},
  component: HeadsetSignIn,
});

/** Signing a VR headset in by phone: a code here, an approval there.
 *
 * Public, like `/signed-out`: it sits outside `_console` because the session gate there
 * would send the headset to a password form, which is exactly what this page replaces.
 * A code expires on its own and the page simply shows a fresh one, as a TV does. */
function HeadsetSignIn() {
  const { next } = Route.useSearch();
  const destination = safeNext(next);
  const [attempt, setAttempt] = useState(0);
  const device = headsetName(navigator.userAgent) ?? "Headset";

  const code = useQuery({
    queryKey: authQueryKeys.global("headset-sign-in", attempt),
    queryFn: () => beginHeadsetSignIn(device),
    ...queryPolicies.immutable,
    meta: NOT_PERSISTED,
  });

  const open = useMutation({
    mutationFn: openHeadsetSession,
    onSuccess: () => window.location.assign(destination),
  });

  // Polling stops the moment the approval is in hand: the exchange is spent then.
  const poll = useQuery({
    queryKey: authQueryKeys.global("headset-sign-in", attempt, "poll"),
    queryFn: () => pollHeadsetSignIn(code.data!),
    enabled: code.data !== undefined && open.isIdle,
    ...queryPolicies.reference,
    meta: NOT_PERSISTED,
    refetchInterval: (query) => pollEvery(query.state.data, code.data?.intervalS),
  });

  const state = poll.data?.state;
  const accessToken =
    poll.data?.state === "approved" ? poll.data.accessToken : undefined;
  useEffect(() => {
    if (accessToken !== undefined && open.isIdle) open.mutate(accessToken);
  }, [accessToken, open]);

  // A code that ran out is replaced, never shown as a dead end. It is replaced a minute
  // early, by wall clock rather than by a timer's count: a headset that is taken off
  // sleeps its page, a sleeping page's timers do not run, and on waking it must not
  // show — or let anyone scan — the code it had when it went to sleep.
  const now = useWallClock();
  const refreshAt =
    code.data === undefined
      ? undefined
      : codeRefreshAt(code.dataUpdatedAt, code.data.expiresInS);
  const stale = state === "expired" || (refreshAt !== undefined && now >= refreshAt);
  useEffect(() => {
    if (stale && open.isIdle) setAttempt((n) => n + 1);
  }, [stale, open.isIdle]);
  const current = stale ? undefined : code.data;
  // After a phone's QR is read, the phone asks for half a minute. Its "No" shows a new QR,
  // which this page reads again, so the reader keeps running all along.
  const [phoneRead, setPhoneRead] = useState<{ code: string; at: number } | null>(null);
  const readPhone =
    phoneRead !== null && phoneRead.code === current?.userCode && now - phoneRead.at < 30_000;

  const otherWay = `${doorPrefix("auth")}/login?next=${encodeURIComponent(
    new URL(destination, window.location.origin).href,
  )}`;

  if (code.isError) {
    return (
      <main className="grid h-full place-items-center p-6">
        <QueryErrorState
          title="Could not get a sign-in code"
          error={code.error}
          retry={() => void code.refetch()}
        />
      </main>
    );
  }

  return (
    <main className="grid h-full place-items-center p-6">
      <section className="flex w-full max-w-3xl flex-col gap-8">
        <h1 className="text-3xl font-semibold">Sign in this headset</h1>
        <div className="flex flex-wrap items-center gap-10">
          <div className="grid size-72 place-items-center rounded-md bg-white p-4">
            {current === undefined ? null : (
              <QRCodeSVG
                key={current.userCode}
                value={current.verificationUriComplete}
                size={256}
                level="M"
                aria-label="Scan to sign in"
              />
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-5">
            <p className="text-xl">
              On your phone, open the sx app, then Me, then{" "}
              <strong className="font-semibold">Sign in a headset</strong>,
              and look at the code it shows. Or scan this code, or open{" "}
              <strong className="font-semibold">
                {current === undefined
                  ? "…"
                  : shortAddress(current.verificationUri)}
              </strong>{" "}
              on your phone or laptop and type this code:
            </p>
            <p
              className="text-4xl font-semibold tabular-nums tracking-[0.18em]"
              aria-label="Sign-in code"
            >
              {current?.userCode ?? "…"}
            </p>
            <Waiting
              state={state}
              readPhone={readPhone}
              opening={open.isPending || open.isSuccess}
              failed={open.isError || poll.isError}
            />
            {current === undefined ? null : (
              <PhoneReader
                code={current}
                onClaimed={() => setPhoneRead({ code: current.userCode, at: Date.now() })}
              />
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {open.isError ? (
            <Button
              size="sm"
              onClick={() => {
                open.reset();
                setAttempt((n) => n + 1);
              }}
            >
              Try again
            </Button>
          ) : null}
          <Button asChild size="sm" variant="ghost">
            <a href={otherWay}>Sign in another way</a>
          </Button>
        </div>
      </section>
    </main>
  );
}

function Waiting({
  state,
  readPhone,
  opening,
  failed,
}: {
  readonly state: HeadsetPoll["state"] | undefined;
  readonly readPhone: boolean;
  readonly opening: boolean;
  readonly failed: boolean;
}) {
  if (failed) {
    return <StatusDot tone="error" label="Sign-in did not finish. Try again." />;
  }
  if (opening || state === "approved") {
    return <StatusDot tone="ok" label="Approved. Opening the console…" />;
  }
  if (readPhone) {
    return (
      <StatusDot tone="live" label="Check your phone: tap Yes if it shows this code." />
    );
  }
  return <StatusDot tone="live" label="Waiting for you to approve it" />;
}

/** The headset's own camera, reading the sx phone app's QR (Quest Browser shows its
 * passthrough cameras to `getUserMedia`). Where there is no camera or the person says no,
 * nothing is shown and the code above still works. */
function PhoneReader({
  code,
  onClaimed,
}: {
  readonly code: HeadsetCode;
  readonly onClaimed: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [live, setLive] = useState(false);
  const claimed = useRef(onClaimed);
  claimed.current = onClaimed;

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer = 0;
    let stopped = false;
    let claiming = false;
    const tried = new Set<string>();
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d", { willReadFrequently: true });
    const stop = () => stream?.getTracks().forEach((track) => track.stop());
    void (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia || context === null) return;
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!devices.some((d) => d.kind === "videoinput")) return;
        const [{ default: jsQR }, media] = await Promise.all([
          import("jsqr"),
          navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
            audio: false,
          }),
        ]);
        stream = media;
        if (stopped || video.current === null) {
          stop();
          return;
        }
        video.current.srcObject = media;
        await video.current.play();
        setLive(true);
        const tick = () => {
          if (stopped) return;
          const v = video.current;
          if (v !== null && v.videoWidth > 0 && !claiming) {
            const scale = Math.min(1, 640 / v.videoWidth);
            canvas.width = Math.round(v.videoWidth * scale);
            canvas.height = Math.round(v.videoHeight * scale);
            context.drawImage(v, 0, 0, canvas.width, canvas.height);
            const frame = context.getImageData(0, 0, canvas.width, canvas.height);
            const found = jsQR(frame.data, frame.width, frame.height, {
              inversionAttempts: "attemptBoth",
            });
            // Only a phone held up to this headset: its QR fills a fifth of the view or
            // more. Another headset across the room may see the same phone; it must not
            // take the phone's question.
            const close =
              found !== null &&
              Math.hypot(
                found.location.topRightCorner.x - found.location.topLeftCorner.x,
                found.location.topRightCorner.y - found.location.topLeftCorner.y,
              ) >=
                canvas.width / 5;
            const offer = found === null || !close ? null : pairOfferFrom(found.data);
            if (offer !== null && !tried.has(offer)) {
              claiming = true;
              void claimPhoneOffer(offer, code)
                .then((ok) => {
                  // A gone offer is not tried again; a lost answer is, on the next frame.
                  tried.add(offer);
                  if (ok && !stopped) claimed.current();
                })
                .catch(() => undefined)
                .finally(() => {
                  claiming = false;
                });
            }
          }
          timer = window.setTimeout(tick, 200);
        };
        tick();
      } catch {
        // No camera, or the person said no: the code above is the way in.
        stop();
      }
    })();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      stop();
    };
  }, [code]);

  return (
    <div className={live ? "flex items-center gap-4" : "hidden"}>
      <video
        ref={video}
        muted
        playsInline
        className="h-24 w-32 rounded-md bg-black object-cover"
      />
      <p className="text-lg text-muted-foreground">
        Or look at the code in the sx app on your phone.
      </p>
    </div>
  );
}

/** The wall clock, re-read every second and the moment the page is seen again. */
function useWallClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = () => setNow(Date.now());
    const timer = window.setInterval(tick, 1000);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    window.addEventListener("pageshow", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
      window.removeEventListener("pageshow", tick);
    };
  }, []);
  return now;
}

/** The door's own pace, slowed by five seconds each time it asks (RFC 8628 §3.5). */
function pollEvery(
  data: HeadsetPoll | undefined,
  intervalS: number | undefined,
): number | false {
  if (data?.state === "approved" || data?.state === "expired") return false;
  const base = Math.max((intervalS ?? 5) * 1000, LIVE_POLL_MS);
  return data?.state === "slow_down" ? base + 5_000 : base;
}
